import React from 'react';

import { useQuery } from '@apollo/client';
import { styled } from '@mui/material';
import { useParams } from 'react-router-dom';

import Disclaimer from '~/components/Summary/Disclaimer';
import Summary from '~/components/Summary/Summary';
import { useWithoutNavigation } from '~/context/OnlineContentConnectorContext';
import { GET_CONTINUE_WATCHING_VIDEO_LINKS, GET_VIDEOS_BY_EXPERT } from '~/graphql/videos';
import useIsSSR from '~/hooks/useIsSSR';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import Expert from '~/pages/SingleVideoPage/components/Expert/Expert';
import JoinNow from '~/pages/SingleVideoPage/components/JoinNow';
import LogoutSection from '~/pages/SingleVideoPage/components/LogoutSection/LogoutSection';
import Overview from '~/pages/SingleVideoPage/components/Overview/Overview';
import VideoTabs from '~/pages/SingleVideoPage/components/VideoTabs/VideoTabs';
import { hasCustomLogoByVideoSlug, TabsEnum } from '~/pages/SingleVideoPage/const';
import { css } from '~/styled';
import { LARGE_MARGIN_PX, SUBMIDDLE_MARGIN_PX, TINY_MARGIN_PX } from '~/theme';
import { ContinueWatchingVideoLink, Query, QueryListContinuingWatchingLinksArgs } from '~/types';
import { generateContinueWatchingLink } from '~/utils/urlUtils/generateContinueWatchingLink';

export const getLinksObj = (arr?: (ContinueWatchingVideoLink | null)[] | null) => {
  let res = {};
  if (arr && Array.isArray(arr)) {
    arr.forEach((item) => {
      if (item?.video?.id) {
        res[item?.video?.id] = {
          chapter: item?.chapter,
          time: item?.time,
        };
      }
    });
  }

  return res;
};

const VideoTabsContainer = ({
  loading,
  video,
  userId,
  isXApiPage,
  showTabs,
  tabs,
  tabIndex,
  setTabIndex,
  availableForFree,
  videoReview,
}) => {
  const { t } = useTenantTranslation();
  const withoutNavigation = useWithoutNavigation();
  const isSSR = useIsSSR();
  const params = useParams();

  const { data, loading: loadingVideosByExpert } = useQuery(GET_VIDEOS_BY_EXPERT, {
    variables: {
      id: video?.user?.id,
    },
    skip: !video?.user?.id || isSSR,
  });

  const listVideos = data?.listVideos;
  const videos = listVideos ? listVideos.filter((item) => item.id && item.id !== video.id) : [];
  const continueWatchingIds = videos ? videos.map((item) => item?.id).filter((item) => !!item) : [];

  const { data: linksData, loading: loadingLinks } = useQuery<
    Pick<Query, 'listContinuingWatchingLinks'>,
    QueryListContinuingWatchingLinksArgs
  >(GET_CONTINUE_WATCHING_VIDEO_LINKS, {
    variables: {
      filter: {
        user_id: userId ? userId : '',
        video_ids: continueWatchingIds as string[],
      },
    },
    skip: !userId || loadingVideosByExpert || continueWatchingIds.length === 0,
  });

  const lastPositionData = getLinksObj(linksData?.listContinuingWatchingLinks);

  const videosWithUrl = videos.map((item) => ({
    ...item,
    url: generateContinueWatchingLink(
      `/videos/${item?.video_url}`,
      lastPositionData[item?.id]?.chapter,
      lastPositionData[item?.id]?.time,
    ),
  }));

  const overviewExists = tabs[TabsEnum.Overview] || tabs[TabsEnum.Overview] === 0;
  const summaryExists = tabs[TabsEnum.Summary] || tabs[TabsEnum.Summary] === 0;

  const joinNowLayout = (
    <StyledJoinNowBlock
      showForDanske={hasCustomLogoByVideoSlug(params.slug)}
      loading={loading}
      availableForFree={availableForFree}
      additionalCss={{
        lockIconCss: generatedLockIcon,
        lockTextCss: generatedLockText,
        descriptionCss: generatedDescription,
      }}
    />
  );

  return (
    <div>
      {!showTabs ? (
        <>
          {overviewExists && (
            <LogoutSection loading={loading} title={t('common.overview')}>
              <Overview
                loading={loading}
                keyLearningObjectives={video.key_learning_objectives}
                summary={video.executive_summary}
                skills={video?.skills}
                impacts={video?.impacts}
              >
                <Disclaimer />
              </Overview>
            </LogoutSection>
          )}
          {joinNowLayout}
          {summaryExists && (
            <>
              <LogoutSection loading={loading} title={t('common.summary')}>
                <Summary summary={video.summary}>
                  <Disclaimer />
                </Summary>
              </LogoutSection>
              {joinNowLayout}
            </>
          )}
          <LogoutSection
            loading={loading || loadingVideosByExpert || isSSR}
            title={t('common.expert')}
          >
            <StyledExpert
              loading={loading || loadingVideosByExpert}
              expert={video?.user}
              videos={videosWithUrl}
              restrictNavigation={isXApiPage || withoutNavigation}
            />
          </LogoutSection>
        </>
      ) : (
        <VideoTabs
          loading={loading || loadingLinks}
          video={video}
          isXApiPage={isXApiPage}
          videosWithUrl={videosWithUrl}
          tabs={tabs}
          overviewExists={overviewExists}
          summaryExists={summaryExists}
          tabIndex={tabIndex}
          setTabIndex={setTabIndex}
          videoReview={videoReview}
        />
      )}
    </div>
  );
};

export default VideoTabsContainer;

const StyledExpert = styled(Expert)`
  margin-bottom: ${LARGE_MARGIN_PX};
`;
const StyledJoinNowBlock = styled(JoinNow)`
  margin: ${LARGE_MARGIN_PX} 0;
`;
const generatedLockIcon = ({ theme }) => css`
  margin-top: 10px;

  ${theme.breakpoints.up('md')} {
    margin-top: 0;
  }
`;
const generatedLockText = ({ theme }) => css`
  font-size: 18px;
  margin: 0px 0px ${TINY_MARGIN_PX};

  ${theme.breakpoints.up('md')} {
    margin: 0;
    font-size: 20px;
    max-width: 85px;
  }
`;
const generatedDescription = ({ theme }) => css`
  font-size: 16px;
  margin-bottom: ${SUBMIDDLE_MARGIN_PX};

  ${theme.breakpoints.up('md')} {
    font-size: 18px;
    margin: 0 ${SUBMIDDLE_MARGIN_PX};
  }
`;
